/* eslint consistent-return: "off" */
import _ from 'lodash';

import utilities from '../utilities/utilities';

// Format a unix timestamp (seconds) as MM/DD/YYYY in local time. Inlined to keep
// the heavy `moment` library out of the eager bundle — this helper is reachable
// from the (eager) map saga, so importing moment here pulled ~64 KB gzip of
// date code onto the critical path for a single date format.
const formatUnixDate = (unixSeconds) => {
  const date = new Date(unixSeconds * 1000);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${date.getFullYear()}`;
};

/**
 * Get all user's licenses.
 *
 * @param {*} userData
 *   User object.
 */
export const propzoneLicenses = _.memoize((userData) => {
  const licenses = [];
  _.each(_.get(userData, 'licenses'), (license, licenseKey) => {
    if (licenseKey.indexOf('propzone') > -1) {
      licenses.push(licenseKey.replace('propzone-', ''));
    }
  });
  if (utilities.isLoggedIn(userData)) {
    // Add Default plan to all logged in users.
    licenses.unshift('basic');
  }
  else {
    // Add Default plan to anonymous users.
    licenses.unshift('anonymous');
  }
  return licenses;
});

/**
 * Check if current subscription has filters applied limit.
 *
 * @param {*} userData
 *   User object.
 * @param {*} subscriptionSettings
 *   Subscripton access settings object.
 */
export const appliedFiltersLimit = _.memoize((userData, subscriptionSettings) => {
  const licenses = propzoneLicenses(userData);
  const filtersLimitSettings = _.get(subscriptionSettings, ['filters', 'additional_settings', 'filters_limit']);

  let currentLimit = 0;
  _.each(licenses, (license) => {
    const limit = parseInt(_.get(filtersLimitSettings, license, 0), 10);
    if (limit > currentLimit) {
      currentLimit = limit;
    }
    else if (limit === -1) {
      currentLimit = limit;
      return false;
    }
  });
  return currentLimit;
});

/**
 * Check if current subscription has property results limit.
 *
 * @param {*} userData
 *   User object.
 * @param {*} subscriptionSettings
 *   Subscripton access settings object.
 */

export const checkPropertyAdditionalSettingsLimit = _.memoize((settingName, userData, subscriptionSettings) => {
  const licenses = propzoneLicenses(userData);
  const filtersLimitSettings = _.get(subscriptionSettings, ['property', 'additional_settings', settingName]);

  let currentLimit = 0;
  _.each(licenses, (license) => {
    const limit = parseInt(_.get(filtersLimitSettings, license, 0), 10);
    if (limit > currentLimit) {
      currentLimit = limit;
    }
    else if (limit === -1) {
      currentLimit = limit;
      return false;
    }
  });
  return currentLimit;
});

export const propertyResultsLimit = _.memoize((userData, subscriptionSettings) =>
  checkPropertyAdditionalSettingsLimit('results_limit', userData, subscriptionSettings));

export const checkDownloadLimit = _.memoize((userData, subscriptionSettings) =>
  checkPropertyAdditionalSettingsLimit('download_limit', userData, subscriptionSettings));

export const getSubscriptionMonthEndDate = _.memoize((userData) => {
  let monthlyEndDate = null;
  _.each(_.get(userData, 'licenses'), (license, licenseKey) => {
    if (licenseKey.indexOf('propzone') > -1) {
      monthlyEndDate = license?.monthly_range?.end_date || null;
      return false;
    }
  });
  return monthlyEndDate ? formatUnixDate(monthlyEndDate) : null;
});

export const hasFreeViewAccess = _.memoize((userData, subscriptionSettings) => {
  const licenses = propzoneLicenses(userData);
  const freeViews = parseInt(_.get(userData, 'free_property_views', 0), 10) || 0;
  const propertyLimitViews = _.get(subscriptionSettings, ['property', 'additional_settings', 'free_view']);
  let currentLimit = 0;
  _.each(licenses, (license) => {
    const limit = parseInt(_.get(propertyLimitViews, license, 0), 10);
    if (limit > currentLimit) {
      currentLimit = limit;
    }
    else if (limit === -1) {
      currentLimit = limit;
      return false;
    }
  });
  if (currentLimit > 0 && currentLimit > freeViews) {
    return true;
  }
  return false;
});

/**
 * Help function to check access per subscription.
 *
 * @param {*} userData
 *   USer object.
 * @param {*} settings
 *   Access settings for particular object.
 * @param {*} freeAccessLicense
 *   Free access license name, in some cases user has posibility to free access.
 */
const checkAccessPerSubscription = (userData, settings, freeAccessLicense = false) => {
  const licenses = propzoneLicenses(userData);
  let hasAccess = false;
  const roles = userData?.roles || [];
  if (roles.includes('staff') || userData?.is_admin) {
    return true;
  }
  _.each(licenses, (license) => {
    const planAccess = _.get(settings, license);
    if (planAccess) {
      hasAccess = planAccess;
      return false;
    }
  });
  // Check if user has free access by some license type.
  if (!hasAccess && freeAccessLicense) {
    return _.get(settings, freeAccessLicense);
  }
  return hasAccess;
};

/**
 * Check if user has access to filter.
 *
 * @param {*} userData
 *   User object.
 * @param {*} subscriptionSettings
 *   Subscripton access settings object.
 * @param {*} filterName
 *   Filter machine name.
 */
export const hasFilterAccess = (userData, subscriptionSettings, filterName) =>
  checkAccessPerSubscription(userData, subscriptionSettings?.filters?.filters_access?.[filterName]);

/**
 * Check if user has access to fields in property panel.
 *
 * @param {*} userData
 *   User object.
 * @param {*} subscriptionSettings
 *   Subscripton access settings object.
 * @param {*} requestGroup
 *   Request group from request settings.
 * @param {*} fieldKey
 *   Field key, like "_property_data|title".
 */
export const hasFieldAccess = (userData, subscriptionSettings, requestGroup, fieldKey, freeAccessLicense = null) => {
  // eslint-disable-next-line
  const fieldAccessSettings = _.get(subscriptionSettings, ['property', 'fields_access', requestGroup, fieldKey]);
  // Return TRUE if field missing in settings, it may ocur for some fields which needed for base
  // request(which doesn't have titles in example).
  if (!fieldAccessSettings) {
    return true;
  }
  return checkAccessPerSubscription(userData, fieldAccessSettings, freeAccessLicense);
};

/**
 *  Check if user has access to additional fields, like result card fileds and top propperty panel fields.
 *
 * @param {*} userData
 *   USer object.
 * @param {*} subscriptionSettings
 *   Subscripton access settings object.
 * @param {*} fieldKey
 *   Field key, like "_property_data|title".
 */
export const hasAdditionalFieldAccess = (userData, subscriptionSettings, fieldKey, freeAccessLicense) => {
  // eslint-disable-next-line
  const fieldAccessSettings = _.get(subscriptionSettings, ['property', 'fields_access', 'additional', fieldKey]);
  // Return TRUE if field missing in settings, it may ocur for some fields which needed for base
  // request(which doesn't have titles in example).
  if (!fieldAccessSettings) {
    return true;
  }
  return checkAccessPerSubscription(userData, fieldAccessSettings, freeAccessLicense);
};
